<script lang="ts">
const COOKIE_NAME = 'mobile_app_banner_dismissed'
</script>
<script setup lang="ts">
    import { storeToRefs } from 'pinia';
    import { breakpointsTailwind, useBreakpoints } from '@vueuse/core';

    const { t } = useTranslation();
    const { $tracker } = useNuxtApp();

    const { storeUrl, trackingPlatform } = useDevicePlatform();
    const { tenant } = storeToRefs(useAuthStore());

    // Cookie management
    
    const { isCookieSet, setCookie } = useCookieManager();

    // Breakpoint detection (mobile only, < md)
    const breakpoints = useBreakpoints(breakpointsTailwind);
    const isMobile = breakpoints.smaller('md');

    // Banner visibility
    const isVisible = ref(false);

    // Track the "shown" event only once per display session
    let hasTrackedShown = false;

    // Show only on mobile, when the dismiss cookie is not set, and not in test/demo mode
    const shouldShowBanner = (): boolean => !isCookieSet(COOKIE_NAME) && isMobile.value && !tenant.value?.isInTestMode;

    const trackShown = () => {
        if (!isVisible.value || hasTrackedShown) return;

        $tracker.track('mobile_app_banner_shown');
        hasTrackedShown = true;
    };

    onMounted(() => {
        isVisible.value = shouldShowBanner();
        trackShown();
    });

    // Hide on desktop, re-show on mobile if cookie not set
    watch(isMobile, (newIsMobile) => {
        if (!newIsMobile) {
            isVisible.value = false;
        } else if (!isCookieSet(COOKIE_NAME) && !tenant.value?.isInTestMode) {
            isVisible.value = true;
            trackShown();
        }
    });

    const handleDownloadClick = () => {
        $tracker.track('mobile_app_banner_download_click', { platform: trackingPlatform.value });
    };

    const handleClose = () => {
        $tracker.track('mobile_app_banner_close_click');

        isVisible.value = false;
        setCookie(COOKIE_NAME, 'twoWeeks');
    };
</script>

<template>
    <Transition
        enter-active-class="transition-opacity duration-300"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition-opacity duration-300"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
    >
        <div
            v-if="isVisible"
            class="flex w-full items-center gap-[10px] border-b border-[#d4d4d8] bg-white px-[10px] py-[15px]"
        >
            <!-- Close Button -->
            <button
                :aria-label="t('actions.close')"
                class="flex min-h-11 min-w-11 shrink-0 items-center justify-center text-[13px] text-[#09090b]"
                @click="handleClose"
            >
                <i class="fa-regular fa-xmark" />
            </button>

            <!-- App logo -->
            <img
                src="/trustup-app-icon.png"
                :alt="t('mobile_app_banner.app_preview')"
                class="h-10 w-10 shrink-0 rounded-[10px]"
            />

            <!-- Text -->
            <div class="flex min-w-0 flex-1 flex-col gap-[3px] pr-[10px]">
                <p class="font-bricolage text-[13px] font-bold leading-none text-black">
                    {{ t('mobile_app_banner.title') }}
                </p>
                <div class="flex items-center gap-[3px] text-[12px]">
                    <span class="flex items-center gap-px text-[#FFC200]">
                        <i
                            v-for="star in 5"
                            :key="star"
                            class="fa-solid fa-star"
                        />
                    </span>
                    <span class="font-bricolage font-medium text-[#52525b]">
                        {{ t('mobile_app_banner.rating') }}
                    </span>
                </div>
            </div>

            <!-- Install Button -->
            <a
                :href="storeUrl"
                target="_blank"
                rel="noopener noreferrer"
                class="flex h-8 shrink-0 items-center justify-center gap-[5px] rounded-[5px] border border-[#FFC400] bg-[#FFD033] px-[6px] drop-shadow-[0px_1px_1px_rgba(0,0,0,0.1)]"
                @click="handleDownloadClick"
            >
                <i class="fa-regular fa-cloud-arrow-down text-[13px] text-[#3f3f46]" />
                <span class="font-inter text-[12px] font-medium text-[#18181b]">
                    {{ t('mobile_app_banner.install') }}
                </span>
            </a>
        </div>
    </Transition>
</template>
